/**
 * Editing-presence client (Excel/Docs-style "who's here").
 *
 * A single WebSocket per project tracks who is in the workspace and which
 * entity (object / region dialog) each person currently has focused.
 * Components announce focus via setFocus()/clearFocus() and read the live
 * roster via subscribe(). Pairs with the server side in wsServer.js
 * (presence:join / presence:focus / presence:blur / presence:update).
 *
 * Deliberately a module singleton, not a context: any dialog can drop in
 * `usePresenceOn(...)` without the whole tree being wrapped in a provider.
 */
import { useSyncExternalStore } from 'react';
import { isQuickViewId } from './quickViewGuard';
import { BACKEND_URL } from './config';
import { getStoredUser } from './authStorage';
import { getBackendToken } from './apiFetch';

const WS_URL = BACKEND_URL.replace(/^http/, 'ws') + '/ws';

let ws = null;
let curProject = null;
let curUser = null;
let myId = null;
let users = [];                 // [{ userId, userName, color, focus }]
let desiredFocus = null;        // { entityType, entityId } | null — replayed on (re)connect
let desiredLocation = null;     // document key this user is viewing — replayed on (re)connect
let desiredLocationLabel = null; // human-readable name of that document — replayed on (re)connect
let alive = false;
let reconnectTimer = null;
const listeners = new Set();

// Avatar colour palette — a fixed, admin-curated set (Teams-style). Each user
// maps DETERMINISTICALLY onto it (hash % length), so the same person is the
// same colour on every screen and there's no per-user assignment. The admin
// can edit the palette (Admin > Avatar Colours); loadAvatarPalette() refreshes
// it at startup. Keep DEFAULT in sync with backend settingsRoutes DEFAULT_PALETTE.
const DEFAULT_PALETTE = [
  '#5b8def', '#e25a5a', '#2bb673', '#e0b020', '#9b59b6', '#d6519e',
  '#3aa5d1', '#e67e22', '#7bc043', '#16a085', '#c0392b', '#8e44ad',
];
const PALETTE_CACHE_KEY = 'pidly.avatarPalette';

// Seed synchronously from the last-known palette (global, not per-user) so the
// very first render already uses the admin's colours instead of the default.
let AVATAR_PALETTE = DEFAULT_PALETTE;
try {
  const cached = JSON.parse(localStorage.getItem(PALETTE_CACHE_KEY) || 'null');
  if (Array.isArray(cached) && cached.length > 0) AVATAR_PALETTE = cached;
} catch { /* no cache / bad JSON */ }

// Replace the active palette (called once the admin-saved palette is fetched,
// and right after an admin saves). Returns true if it actually changed, so the
// caller can force a re-render of avatar surfaces.
export function setAvatarPalette(colors) {
  if (!Array.isArray(colors) || colors.length === 0) return false;
  if (colors.length === AVATAR_PALETTE.length && colors.every((c, i) => c === AVATAR_PALETTE[i])) {
    return false;
  }
  AVATAR_PALETTE = colors;
  try { localStorage.setItem(PALETTE_CACHE_KEY, JSON.stringify(colors)); } catch { /* quota */ }
  paletteVersion += 1;
  for (const l of paletteListeners) { try { l(); } catch { /* a listener's own error */ } }
  return true;
}

// A palette that lands AFTER a face was drawn must redraw it: avatars that
// call useAvatarPalette() re-render when it changes (one store, not a
// one-time read).
let paletteVersion = 0;
const paletteListeners = new Set();
function subscribePalette(fn) { paletteListeners.add(fn); return () => paletteListeners.delete(fn); }
function getPaletteVersion() { return paletteVersion; }
export function useAvatarPalette() {
  return useSyncExternalStore(subscribePalette, getPaletteVersion, getPaletteVersion);
}

// Deterministic colour for a user key (id/email/name), picked from the palette
// so the same person shows the same colour on every surface.
export function userColor(key) {
  const s = String(key || 'anon');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 1000003;
  const pal = AVATAR_PALETTE.length ? AVATAR_PALETTE : DEFAULT_PALETTE;
  return pal[h % pal.length];
}
const colorFor = userColor;

function notify() {
  for (const l of listeners) { try { l(users); } catch { /* listener errors are their own */ } }
}

function send(obj) {
  try { if (ws && ws.readyState === 1) ws.send(JSON.stringify(obj)); } catch { /* socket closing */ }
}

async function open() {
  if (!alive || !curProject) return;
  const token = await getBackendToken().catch(() => null);
  if (!alive || !curProject) return; // disconnected while resolving the token
  ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token || '')}`);
  ws.onopen = () => {
    send({ type: 'presence:join', projectId: curProject, user: curUser });
    if (desiredFocus) {
      send({ type: 'presence:focus', projectId: curProject, ...desiredFocus });
    }
    if (desiredLocation != null) {
      send({ type: 'presence:location', projectId: curProject, location: desiredLocation, locationLabel: desiredLocationLabel });
    }
  };
  ws.onmessage = (e) => {
    let msg;
    try { msg = JSON.parse(e.data); } catch { return; }
    if (msg.type !== 'presence:update') return;
    if (msg.projectId && msg.projectId !== curProject) return;
    users = Array.isArray(msg.users) ? msg.users : [];
    notify();
  };
  ws.onclose = () => { if (alive) reconnectTimer = setTimeout(open, 2000); };
  ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
}

// Connect (idempotent per project). Mount once at the workspace level.
export function connectPresence(projectId, user) {
  // A Quick View is private and throwaway: nobody is told you are in it.
  if (isQuickViewId(projectId)) { disconnectPresence(); return; }
  const u = user || getStoredUser() || {};
  const id = u.id || u.email || null;
  curUser = {
    id,
    name: u.name || u.displayName || u.email || 'Someone',
    color: colorFor(id),
  };
  myId = id;
  if (curProject === projectId && ws) return; // already on this project
  disconnectPresence();
  curProject = projectId;
  alive = true;
  open();
}

export function disconnectPresence() {
  alive = false;
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  try { ws?.close(); } catch { /* noop */ }
  ws = null;
  curProject = null;
  desiredFocus = null;
  desiredLocation = null;
  desiredLocationLabel = null;
  if (users.length) { users = []; notify(); }
}

// Announce which entity (and optionally which field/row) this client is
// editing. null entityId clears it entirely.
export function setFocus(entityType, entityId, field = null, label = null) {
  desiredFocus = (entityId != null)
    ? { entityType, entityId: String(entityId), field: field || null, label: label || null }
    : null;
  if (desiredFocus) {
    send({ type: 'presence:focus', projectId: curProject, entityType, entityId: String(entityId), field: field || null, label: label || null });
  } else {
    send({ type: 'presence:blur', projectId: curProject });
  }
}

export function clearFocus() { setFocus(null, null); }

// Announce which document this client is viewing (drives the "who's here"
// roster). `label` is a human-readable name for that document, carried in
// the roster so org-scoped surfaces (e.g. the Members popover) can show
// "Viewing <doc>" without needing the project's file list. Pass null when
// leaving the document.
export function setLocation(location, label = null) {
  const loc = location != null ? String(location) : null;
  const lbl = label != null ? String(label) : null;
  if (loc === desiredLocation && lbl === desiredLocationLabel) return;
  desiredLocation = loc;
  desiredLocationLabel = lbl;
  send({ type: 'presence:location', projectId: curProject, location: loc, locationLabel: lbl });
}

export function subscribe(listener) {
  listeners.add(listener);
  listener(users);
  return () => listeners.delete(listener);
}

export function getMyId() { return myId; }
